'use client'

import { useEffect, useState } from 'react'

import { IconLocation, IconNotebook } from '@/app/components/atoms/Icons'
import { Link } from '@/app/components/atoms/Link'
import { Price } from '@/app/components/atoms/Price'
import { PriceTag } from '@/app/components/atoms/PriceTag'
import { PurchaseButton } from '@/app/components/shared/PurchaseButton'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { useSessionContext } from '@/app/context/session'
import { CTA_POSITION_STICKY, NO_APLICA } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { CALENDAR_LINK_LABEL } from '@/app/lib/performance'
import { cx } from '@/app/lib/utils'

import { BENEFIT_ICON_SIZE, BENEFITS, BUY_BOX_LABEL, BUY_BOX_VENUE_MODULE_NAME } from './constants'
import { useBuyBoxVisibility } from './hooks'
import styles from './PerformanceBuyBox.module.scss'
import type { PerformanceBuyBoxProps } from './types'

const META_ICON_SIZE = 24

/**
 * `PerformanceBuyBox` [section] — barra flotante de compra de la ficha,
 * **siempre anclada abajo de la pantalla** (`position: fixed`), nunca en el
 * flujo normal de la página.
 *
 * Se muestra exactamente cuando el CTA de la cabecera **no está en pantalla**:
 * tanto si el usuario todavía no ha llegado a él (cabecera alta, el botón cae
 * por debajo del fold) como si ya lo ha dejado atrás. Mientras el CTA sí se ve,
 * la barra estorba y duplica el mismo botón, así que se retira. También se
 * retira al alcanzar el `<footer>`: no aporta nada seguir insistiendo con el CTA
 * justo cuando el usuario ya ha llegado al final del contenido. Todo ese
 * cálculo vive en `useBuyBoxVisibility`.
 *
 * Único componente cliente de esta sección: necesita `scroll`/`resize` para
 * decidir su propia visibilidad y la sesión para la franja de ventajas. Sin
 * lógica de negocio propia: recibe el mismo `PerformanceHeroViewModel` que la
 * cabecera y solo lo pinta — el precio y el estado del botón ya los resolvieron
 * `resolveEventPrice`/`resolvePurchaseState` (spec.md SC-005).
 */
export function PerformanceBuyBox({ hero, ctaId, analyticsItem }: PerformanceBuyBoxProps) {
  const { title, dateLabel, timeLabel, venueLabel, venueHref, price, purchaseState, purchaseUrl } =
    hero
  const visible = useBuyBoxVisibility(ctaId)
  const analytics = useOptionalPageAnalytics()

  // `click` del enlace al recinto: criterio de `Ubicación Detalles Producto.csv`
  // → "Click en (Localización)" (`content_link`, módulo transversal).
  function trackVenueClick() {
    if (!venueHref || !venueLabel) return
    analytics?.trackClick({
      click_type: 'content_link',
      click_subtype: NO_APLICA,
      link_text: slugify(venueLabel),
      link_url: venueHref,
      link_domain: NO_APLICA,
      module_name: BUY_BOX_VENUE_MODULE_NAME,
      module_type: NO_APLICA,
      nav_level: NO_APLICA,
      nav_path: NO_APLICA,
      action_detail: NO_APLICA,
      action_value: NO_APLICA,
      action_context: NO_APLICA,
    })
  }
  const { isLoggedIn } = useSessionContext()

  // La franja de ventajas arranca SIEMPRE colapsada, también para quien no
  // tiene sesión: `isLoggedIn` vale `false` en servidor y en el primer render
  // de cliente (lo impone ISR, ver `SessionProvider`), así que desplegarla ya
  // en ese primer render se la enseñaría un instante a los usuarios logueados
  // para arrebatársela después. Invirtiendo el orden, quien tiene sesión no ve
  // nada aparecer ni desaparecer, y quien no la tiene ve el despliegue animado.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- marca de post-hidratación; no depende de props ni de estado previo
    setMounted(true)
  }, [])
  const benefitsOpen = mounted && !isLoggedIn

  // «Disponible el …» no lleva precio (no hay nada que comprar todavía), solo
  // el CTA deshabilitado con la fecha de apertura y el recordatorio.
  const showPrice = purchaseState.kind !== 'upcoming'

  return (
    <section
      className={cx(styles.section, visible && styles.visible)}
      aria-hidden={!visible}
      // `aria-hidden` por sí solo no saca del orden de tabulación al enlace o
      // botón que hay dentro: sin `inert`, la barra oculta sigue siendo
      // alcanzable con el teclado.
      inert={!visible}
      aria-label={BUY_BOX_LABEL}
    >
      <div className={cx(styles.wrap, showPrice && !price.tag && styles.wrapSpacious)}>
        {/* Título y datos del evento — desde tablet: la barra de móvil se
            queda en lo imprescindible (precio + CTA), que es lo que pide el
            diseño para esa anchura. */}
        <div className={styles.info}>
          <p className={styles.title}>{title}</p>
          <p className={styles.meta}>
            <span className={styles.metaItem}>
              <IconNotebook size={META_ICON_SIZE} className={styles.metaIcon} />
              {/* Fecha y hora en dos elementos, no en un solo texto: la hora
                  es texto libre del backend y puede llegar larguísima, así que
                  es la única que se recorta (ver `.metaTime`) para que la
                  fecha se vea siempre entera. */}
              <span className={styles.metaText}>
                <span className={styles.metaDate}>{dateLabel}</span>
                {timeLabel && <span className={styles.metaTime}>{` · ${timeLabel}`}</span>}
              </span>
            </span>
            {venueLabel && (
              <span className={styles.metaItem}>
                <IconLocation size={META_ICON_SIZE} className={styles.metaIcon} />
                {venueHref ? (
                  <Link href={venueHref} size="s" onClick={trackVenueClick}>
                    {venueLabel}
                  </Link>
                ) : (
                  <span>{venueLabel}</span>
                )}
              </span>
            )}
          </p>
        </div>

        <div className={styles.purchase}>
          {showPrice && (
            <div className={cx(styles.priceRow, price.tag && styles.priceRowOffer)}>
              {/* `PriceTag` devuelve `null` cuando no hay rebaja ni promoción,
                  así que el badge aparece solo en la variante con oferta sin
                  necesidad de condicional aquí. */}
              <PriceTag tag={price.tag} hasAppPromotions={price.hasAppPromotions} />
              <Price
                amount={price.amount}
                isFree={price.isFree}
                originalAmount={price.originalAmount ?? undefined}
                variant={price.originalAmount != null ? 'offer' : 'default'}
                showFrom
                // El importe rebajado va en negro, no en el rojo de promoción:
                // sobre el blanco de la barra el diseño lo iguala al resto del
                // texto (a diferencia de las cards de listado).
                accentColor
                className={styles.priceValue}
              />
            </div>
          )}

          <PurchaseButton
            purchaseState={purchaseState}
            href={purchaseUrl ?? undefined}
            className={styles.cta}
            analyticsItem={analyticsItem}
            ctaPosition={CTA_POSITION_STICKY}
          />

          {/* Solo en el estado «Disponible el …»: el view model ya resuelve esa
              condición dejando `calendarUrl` a null en el resto. Mismo texto y
              mismo comportamiento que el enlace del hero. */}
          {hero.calendarUrl && (
            <a
              className={styles.calendarLink}
              href={hero.calendarUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {CALENDAR_LINK_LABEL}
            </a>
          )}
        </div>
      </div>

      {/* Ventajas de comprar en El Corte Inglés — desde tablet y solo sin
          sesión: a quien ya ha entrado no hay que convencerle de esto. */}
      <div
        className={cx(styles.benefits, benefitsOpen && styles.benefitsOpen)}
        aria-hidden={!benefitsOpen}
      >
        {/* Envoltorio intermedio obligatorio: es quien recorta (`overflow`) al
            colapsar la fila del grid. La separación y el filete van en la lista
            de dentro, porque un `padding` en el elemento recortado impediría
            que la franja llegue realmente a alto cero. */}
        <div className={styles.benefitsInner}>
          <ul className={styles.benefitsList}>
            {BENEFITS.map(({ Icon, label }) => (
              <li key={label} className={styles.benefit}>
                <Icon size={BENEFIT_ICON_SIZE} className={styles.benefitIcon} />
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
